import { WordsInWordFilter } from "./models";

// create string processor class with static methods
/**
 * StringProcessor class
 * @class
 * @classdesc This class contains all the string processing methods
 * @author Adam Rzymski
 * @version 1.0.0
 * @date 2022-03-29
 * @static
 */
export class StringProcessor {
  /**
   * @description This method converts a string to a NATO phonetic string
   * @param {string} sentence - The string or words to convert
   * @returns {string} The NATO phonetic string
   * @memberof StringProcessor
   * @static
   * @example
   * StringProcessor.natoPhoneticString("Hello World");
   * @example
   * StringProcessor.natoPhoneticString("Hello World");
   * // return the NATO phonetic string
   */
  public static natoPhoneticString = (sentence: string): string => {
    const transformDict: { [key: string | number]: string } = {
      A: "Alpha",
      B: "Bravo",
      C: "Charlie",
      D: "Delta",
      E: "Echo",
      F: "Foxtrot",
      G: "Golf",
      H: "Hotel",
      I: "India",
      J: "Juliett",
      K: "Kilo",
      L: "Lima",
      M: "Mike",
      N: "November",
      O: "Oscar",
      P: "Papa",
      Q: "Quebec",
      R: "Romeo",
      S: "Sierra",
      T: "Tango",
      U: "Uniform",
      V: "Victor",
      W: "Whiskey",
      X: "X-ray",
      Y: "Yankee",
      Z: "Zulu",
      0: "Zero",
      1: "One",
      2: "Two",
      3: "Three",
      4: "Four",
      5: "Five",
      6: "Six",
      7: "Seven",
      8: "Eighth",
      9: "Nine",
    };
    let returnString = "";
    // split string on spaces
    const splitString = sentence.split(" ");

    // loop through each word
    for (const word of splitString) {
      if (word?.length > 0) {
        // regex replace all non-alphanumeric characters with empty word
        const wordNoNonAlphaNumeric = word.replace(/[^a-zA-Z0-9]/g, "");
        for (const letter of wordNoNonAlphaNumeric) {
          returnString += `${transformDict[letter.toUpperCase()].toUpperCase()} `;
        }
        // add new line
        returnString += "\n";
      }
    }
    return returnString;
  };

  /** Check if a string made of multiple fields contains a word
   * @description This method checks if a string made of multiple fields contains a word
   * @param {string} sentence - The string to check
   * @param {string} word - The word to check for
   * @returns {boolean} True if the string contains the word, false if not
   * @memberof StringProcessor
   * @static
   * @example
   * StringProcessor.containsWord("Hello World", "World");
   */

  public static wordsInWordFilter: WordsInWordFilter = (options, input) =>
    options.filter((o: { jobDescription: string; jobTypeCode: string; tradeDescription: string }) =>
      input
        .split(" ")
        .every((v: string) =>
          `${o.jobDescription} ${o.jobTypeCode} ${o.tradeDescription}`.toLowerCase().includes(v.toLowerCase())
        )
    );

  /**
   * @description This method converts a string written in camel case to separate words
   * @param {string} camelWord - The string to convert
   * @returns {string} The camel case string converted to separate words
   * @memberof StringProcessor
   * @static
   * @example
   * StringProcessor.StringProcessor.camelToWords("helloWorld");
   */

  public static camelToWords = (camelWord: string) => camelWord.replace(/([a-z])([A-Z])/g, "$1 $2");
}
